import React from "react";
import {
  FaExclamationTriangle,
  FaLock,
  FaPhoneAlt,
  FaSignOutAlt,
} from "react-icons/fa";
import COLORS from "../resources/colors/COLORS";

const ACCENT = COLORS.primary_color_shade6;
const ACCENT_DARK = COLORS.primary_color_shade8;
const ACCENT_SOFT = COLORS.primary_color_shade0;

//Created By Cursor
function logoutToLogin() {
  try {
    localStorage.removeItem("token");
    localStorage.removeItem("staff_details");
    localStorage.removeItem("SchoolCode");
    localStorage.removeItem("CurrentSession");
  } catch (_) {
    /* ignore */
  }
  window.location.href = "/";
}

//Created By Cursor — full-viewport gate (same footprint as login page)
export default function AccountAccessBlock({
  reason = "inactive",
  schoolName = "",
  schoolCode = "",
  productStatusName = "",
}) {
  const isDemoExpired = reason === "demo_expired";
  const title = isDemoExpired ? "Demo expired" : "Service expired";
  const subtitle = isDemoExpired
    ? "Your free demo period has ended. Please contact EduOK support to activate your school account."
    : "Your service is inactive / expired. Please renew or contact EduOK support to continue.";
  const schoolLabel = schoolName
    ? schoolCode
      ? `${schoolName} (${schoolCode})`
      : schoolName
    : "";

  return (
    <div
      className="eduok-access-gate"
      style={{
        position: "relative",
        minHeight: "100vh",
        width: "100%",
        overflow: "hidden",
        fontFamily: '"Source Sans 3", Inter, system-ui, sans-serif',
        color: "#0f172a",
        background: `linear-gradient(145deg, ${ACCENT_SOFT} 0%, #ffffff 42%, #e0f2fe 100%)`,
      }}
    >
      <style>{`
        @keyframes eduokGateFadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .eduok-access-gate__logout:hover {
          background: #b91c1c !important;
          transform: translateY(-1px);
        }
        .eduok-access-gate__logout:active {
          transform: translateY(0);
        }
        @media (max-width: 640px) {
          .eduok-access-gate__shell {
            padding: 28px 16px !important;
          }
          .eduok-access-gate__card {
            padding: 28px 20px !important;
          }
          .eduok-access-gate__brand {
            font-size: 26px !important;
          }
        }
      `}</style>

      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "radial-gradient(circle at 12% 18%, rgba(3,169,244,0.16) 0%, transparent 34%), radial-gradient(circle at 88% 78%, rgba(2,136,209,0.12) 0%, transparent 36%), linear-gradient(rgba(2,119,189,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(2,119,189,0.04) 1px, transparent 1px)",
          backgroundSize: "auto, auto, 42px 42px, 42px 42px",
          pointerEvents: "none",
        }}
      />

      <div
        className="eduok-access-gate__shell"
        style={{
          position: "relative",
          zIndex: 1,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 28,
          padding: "40px 24px",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            animation: "eduokGateFadeUp 0.55s ease-out both",
          }}
        >
          <img
            src={`${process.env.PUBLIC_URL || ""}/images/logo.png`}
            alt="EduOK"
            style={{
              width: 56,
              height: 56,
              objectFit: "contain",
              borderRadius: 14,
              flexShrink: 0,
            }}
          />
          <div
            className="eduok-access-gate__brand"
            style={{
              fontFamily: '"Outfit", sans-serif',
              fontSize: 32,
              fontWeight: 800,
              letterSpacing: "-0.02em",
              lineHeight: 1.15,
              color: ACCENT_DARK,
            }}
          >
            Edu<span style={{ color: ACCENT }}>OK</span> School Management
            System
          </div>
        </div>

        <section
          className="eduok-access-gate__card"
          role="alert"
          style={{
            width: "min(440px, 100%)",
            padding: "36px 28px 28px",
            borderRadius: 20,
            background: "#fff",
            border: "1px solid rgba(148, 163, 184, 0.28)",
            boxShadow: "0 18px 48px rgba(15, 23, 42, 0.1)",
            textAlign: "center",
            animation: "eduokGateFadeUp 0.65s ease-out 0.08s both",
          }}
        >
          <div
            style={{
              width: 72,
              height: 72,
              margin: "0 auto 16px",
              borderRadius: 20,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              background: isDemoExpired ? "#fff7ed" : "#fee2e2",
              color: isDemoExpired ? "#ea580c" : "#dc2626",
              fontSize: 30,
            }}
            aria-hidden
          >
            {isDemoExpired ? <FaExclamationTriangle /> : <FaLock />}
          </div>

          {schoolName ? (
            <div style={{ margin: "0 0 14px" }}>
              <p
                style={{
                  margin: 0,
                  fontFamily: '"Outfit", sans-serif',
                  fontSize: 18,
                  fontWeight: 800,
                  letterSpacing: "-0.01em",
                  lineHeight: 1.3,
                  color: ACCENT_DARK,
                  wordBreak: "break-word",
                }}
              >
                {schoolName}
              </p>
              {schoolCode ? (
                <p
                  style={{
                    margin: "6px 0 0",
                    fontSize: 12,
                    fontWeight: 700,
                    color: ACCENT,
                    letterSpacing: "0.04em",
                  }}
                >
                  School code · {schoolCode}
                </p>
              ) : null}
            </div>
          ) : schoolLabel ? (
            <p
              style={{
                margin: "0 0 10px",
                fontSize: 15,
                fontWeight: 800,
                color: ACCENT_DARK,
              }}
            >
              {schoolLabel}
            </p>
          ) : null}

          <p
            style={{
              margin: "0 0 10px",
              fontSize: 12,
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: isDemoExpired ? "#c2410c" : "#b91c1c",
            }}
          >
            {productStatusName || (isDemoExpired ? "Demo" : "Inactive")}
          </p>

          <h1
            style={{
              margin: "0 0 10px",
              fontFamily: '"Outfit", sans-serif',
              fontSize: 28,
              fontWeight: 800,
              letterSpacing: "-0.02em",
              color: "#0f172a",
              lineHeight: 1.2,
            }}
          >
            {title}
          </h1>

          <p
            style={{
              margin: "0 auto",
              maxWidth: 340,
              fontSize: 15,
              fontWeight: 500,
              color: "#475569",
              lineHeight: 1.55,
            }}
          >
            {subtitle}
          </p>

          <div
            style={{
              marginTop: 18,
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              fontSize: 13,
              fontWeight: 700,
              color: "#9a3412",
            }}
          >
            <FaPhoneAlt aria-hidden />
            Contact EduOK support to continue
          </div>

          <div style={{ marginTop: 24 }}>
            <button
              type="button"
              className="eduok-access-gate__logout"
              onClick={logoutToLogin}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                minHeight: 48,
                width: "100%",
                padding: "0 18px",
                borderRadius: 12,
                border: 0,
                background: "#dc2626",
                color: "#fff",
                fontSize: 15,
                fontWeight: 700,
                cursor: "pointer",
                transition: "background 0.15s ease, transform 0.15s ease",
              }}
            >
              <FaSignOutAlt aria-hidden />
              Logout
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}

//Created By Cursor
export function resolveProductAccess(productAccount) {
  const row = Array.isArray(productAccount) ? productAccount[0] : null;
  if (!row) {
    return { allow: true, reason: null, demoDays: null, status: "" };
  }

  const status = String(row.product_status ?? "").trim();
  const statusName = String(row.product_status_name || "").trim();
  const demoService = String(row.demo_service ?? "").trim();
  const demoDaysRaw = row.demo_remaining_days;
  const demoDays =
    demoDaysRaw === null || demoDaysRaw === undefined || demoDaysRaw === ""
      ? null
      : Math.max(0, Number(demoDaysRaw));

  if (status === "2") {
    //Created By Cursor — billed Active, or post–service_end still inside grace
    const inGrace =
      String(row.in_grace ?? "") === "1" ||
      String(row.subscription_status || "").toLowerCase() === "grace";
    const graceDaysRaw = row.grace_remaining_days;
    const graceDays =
      graceDaysRaw === null || graceDaysRaw === undefined || graceDaysRaw === ""
        ? null
        : Math.max(0, Number(graceDaysRaw));
    return {
      allow: true,
      reason: null,
      demoDays: null,
      status,
      statusName: statusName || "Active",
      inGrace,
      graceDays: inGrace && Number.isFinite(graceDays) ? graceDays : null,
    };
  }

  if (status === "1") {
    if (demoService === "1") {
      return {
        allow: true,
        reason: null,
        demoDays: Number.isFinite(demoDays) ? demoDays : null,
        status,
        statusName: statusName || "Demo",
      };
    }
    return {
      allow: false,
      reason: "demo_expired",
      demoDays: 0,
      status,
      statusName: statusName || "Demo",
    };
  }

  if (status === "3") {
    return {
      allow: false,
      reason: "service_expired",
      demoDays: null,
      status,
      statusName: statusName || "InActive",
    };
  }

  return {
    allow: false,
    reason: "service_expired",
    demoDays: null,
    status,
    statusName: statusName || "InActive",
  };
}
